import { useNavigate } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';

const sections = [
  {
    id: 1,
    icon: 'ri-time-line',
    color: 'amber',
    title: 'Puntualidad y gestión del tiempo',
    content: [
      {
        subtitle: 'La puntualidad es innegociable',
        text: 'En Alemania, llegar a tiempo significa llegar 5 minutos antes. La puntualidad no es solo una cortesía, es una señal de respeto y profesionalidad. Llegar tarde a una reunión, entrevista o primer día de trabajo puede dañar gravemente tu reputación desde el inicio. Si prevés un retraso, avisa con antelación.',
      },
      {
        subtitle: 'Planificación y estructura',
        text: 'Los alemanes planifican con mucha antelación. Las reuniones tienen agenda previa, los proyectos tienen cronogramas detallados y se espera que los cumplan. Improvisar o cambiar planes de última hora genera desconfianza. Aprende a trabajar con calendarios compartidos y a respetar los plazos como compromisos firmes.',
      },
      {
        subtitle: 'Separación entre trabajo y vida personal',
        text: 'Aunque los alemanes son muy eficientes en horario laboral, respetan estrictamente el tiempo personal. No se espera que respondas emails fuera del horario de trabajo. El concepto de "Feierabend" (fin de la jornada laboral) es sagrado. Esta separación es saludable y debes respetarla también en los demás.',
      },
    ],
  },
  {
    id: 2,
    icon: 'ri-speak-line',
    color: 'emerald',
    title: 'Comunicación directa y honesta',
    content: [
      {
        subtitle: 'La comunicación directa no es mala educación',
        text: 'Los alemanes son conocidos por su comunicación directa y sin rodeos. Un "no" es un "no", y un "sí" es un compromiso real. No interpretes la franqueza como agresividad o falta de tacto; es simplemente eficiencia comunicativa. Aprende a expresar tu opinión con claridad y sin ambigüedades.',
      },
      {
        subtitle: 'El desacuerdo es bienvenido',
        text: 'En el entorno laboral alemán, expresar una opinión contraria de forma argumentada es señal de competencia, no de conflicto. Se espera que los profesionales defiendan sus posiciones con datos y razonamientos sólidos. Evita el "sí" automático para complacer; si no estás de acuerdo, dilo con respeto y argumentos.',
      },
      {
        subtitle: 'Formalidad inicial',
        text: 'Hasta que no te inviten a tutear (usar "du" en lugar de "Sie"), mantén el tratamiento formal. Usa "Herr" o "Frau" seguido del apellido. En muchas empresas modernas y startups se usa el "du" desde el principio, pero en entornos más tradicionales o corporativos, espera a que sea el colega de mayor rango quien proponga el cambio.',
      },
    ],
  },
  {
    id: 3,
    icon: 'ri-building-4-line',
    color: 'sky',
    title: 'Estructura jerárquica y toma de decisiones',
    content: [
      {
        subtitle: 'Jerarquía clara pero accesible',
        text: 'Las empresas alemanas tienen estructuras jerárquicas bien definidas, pero los directivos suelen ser accesibles y abiertos al diálogo. Las decisiones importantes se toman en los niveles adecuados y se espera que cada profesional asuma la responsabilidad de su área sin necesidad de supervisión constante.',
      },
      {
        subtitle: 'Proceso de toma de decisiones',
        text: 'Las decisiones en Alemania se toman de forma deliberada y consensuada. Puede parecer lento comparado con otros países, pero una vez tomada la decisión, la implementación es rápida y eficiente. No presiones para acelerar decisiones; el proceso de análisis previo es parte del rigor alemán.',
      },
      {
        subtitle: 'Responsabilidad individual',
        text: 'Se espera que cada empleado sea autónomo en su área de responsabilidad. No esperes que tu jefe te diga qué hacer en cada momento. Toma iniciativa, propón soluciones y comunica los avances de forma proactiva. La dependencia excesiva del superior se percibe como falta de competencia.',
      },
    ],
  },
  {
    id: 4,
    icon: 'ri-file-list-3-line',
    color: 'rose',
    title: 'Documentación y precisión',
    content: [
      {
        subtitle: 'La cultura del papel y la documentación',
        text: 'Alemania tiene una fuerte cultura de documentación escrita. Los acuerdos verbales se confirman por escrito, los procesos se documentan y los contratos se leen con detalle. Guarda todos los documentos importantes: contratos, nóminas, correspondencia oficial. La burocracia alemana es extensa pero predecible.',
      },
      {
        subtitle: 'Precisión en el trabajo',
        text: 'La calidad y la precisión son valores fundamentales en el entorno laboral alemán. "Gut genug" (suficientemente bueno) no es el estándar; se busca la excelencia. Revisa tu trabajo antes de entregarlo, verifica los datos y asegúrate de que los informes y presentaciones sean impecables.',
      },
      {
        subtitle: 'Títulos y certificaciones',
        text: 'Los títulos académicos y las certificaciones profesionales tienen mucho peso en Alemania. Si tienes un doctorado, se usa en la firma y en las tarjetas de visita. Las certificaciones técnicas y los diplomas de formación continua son muy valorados. Asegúrate de tener tus títulos homologados si es necesario.',
      },
    ],
  },
  {
    id: 5,
    icon: 'ri-team-line',
    color: 'teal',
    title: 'Integración en el equipo',
    content: [
      {
        subtitle: 'Relaciones profesionales vs. personales',
        text: 'Los alemanes suelen separar claramente las relaciones profesionales de las personales. No esperes hacer amigos íntimos en el trabajo de inmediato. Las relaciones se construyen lentamente, basadas en la confianza y el respeto mutuo. Una vez ganada esa confianza, es duradera y sólida.',
      },
      {
        subtitle: 'Actividades sociales de empresa',
        text: 'Participar en las actividades sociales del equipo (almuerzos, eventos de empresa, el "Feierabend" con colegas) es importante para la integración. Aunque no son obligatorias, rechazarlas sistemáticamente puede interpretarse como desinterés por el equipo. Participa con naturalidad y sin forzar la cercanía.',
      },
      {
        subtitle: 'El idioma alemán como ventaja',
        text: 'Aunque muchas empresas trabajan en inglés, aprender alemán es una inversión que marca una diferencia enorme. Demuestra compromiso con el país y la cultura. Incluso un nivel B1-B2 te abrirá puertas que el inglés no puede. Los colegas alemanes aprecian enormemente el esfuerzo por aprender su idioma.',
      },
    ],
  },
  {
    id: 6,
    icon: 'ri-heart-pulse-line',
    color: 'indigo',
    title: 'Bienestar, derechos laborales y vida en Alemania',
    content: [
      {
        subtitle: 'Sistema de bienestar social',
        text: 'Alemania tiene uno de los sistemas de bienestar social más completos de Europa: seguro médico obligatorio, seguro de desempleo, pensiones y bajas por enfermedad remuneradas. Como trabajador, contribuirás a estos sistemas y tendrás derecho a sus prestaciones. Infórmate sobre el sistema de Krankenversicherung (seguro médico) desde el primer día.',
      },
      {
        subtitle: 'Vacaciones y conciliación',
        text: 'El mínimo legal de vacaciones en Alemania es de 20 días hábiles (con semana de 5 días), aunque la mayoría de convenios ofrecen 25-30 días. Las bajas por enfermedad están bien reguladas: los primeros 6 semanas las paga el empleador al 100%. La conciliación familiar está muy protegida por ley.',
      },
      {
        subtitle: 'Coste de vida y salarios',
        text: 'El coste de vida varía mucho según la ciudad: Múnich y Fráncfort son las más caras; ciudades como Leipzig, Dresde o Hannover son más asequibles. El salario mínimo en 2024 es de 12,41€/hora. Investiga el rango salarial de tu sector y ciudad antes de negociar. Los salarios netos son significativamente menores que los brutos por las cotizaciones sociales.',
      },
    ],
  },
];

const tips = [
  { icon: 'ri-check-double-line', text: 'Llega siempre 5 minutos antes a cualquier cita profesional' },
  { icon: 'ri-check-double-line', text: 'Aprende al menos alemán básico (A2-B1) antes de llegar' },
  { icon: 'ri-check-double-line', text: 'Confirma siempre los acuerdos por escrito' },
  { icon: 'ri-check-double-line', text: 'Respeta el "Feierabend": no envíes emails fuera de horario' },
  { icon: 'ri-check-double-line', text: 'Homologa tus títulos académicos antes de buscar empleo' },
  { icon: 'ri-check-double-line', text: 'Abre una cuenta bancaria alemana en cuanto llegues' },
];

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
  amber: { bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
  sky: { bg: 'bg-sky-600', text: 'text-sky-600', border: 'border-sky-200', light: 'bg-sky-50' },
  rose: { bg: 'bg-rose-600', text: 'text-rose-600', border: 'border-rose-200', light: 'bg-rose-50' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
  indigo: { bg: 'bg-indigo-600', text: 'text-indigo-600', border: 'border-indigo-200', light: 'bg-indigo-50' },
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Claves culturales para trabajar en Alemania',
  description: 'Aspectos esenciales del entorno laboral alemán: puntualidad, jerarquía, comunicación directa y expectativas profesionales para integrarse con éxito en el mercado laboral alemán.',
  url: `${siteUrl}/guia-claves-alemania`,
  author: { '@type': 'Organization', name: 'BN ĀGENZIA' },
  publisher: { '@type': 'Organization', name: 'BN ĀGENZIA', url: siteUrl },
  inLanguage: 'es',
  about: { '@type': 'Thing', name: 'Mercado laboral Alemania' },
  dateModified: '2025-07-14',
};

export default function GuiaClavesAlemaniaPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 print:bg-white">
      <SeoHead
        title="Claves culturales para trabajar en Alemania – Guía | BN ĀGENZIA"
        description="Puntualidad, jerarquía, comunicación directa y derechos laborales en Alemania. Todo lo que necesitas saber para integrarte con éxito en el mercado laboral alemán."
        keywords="trabajar en Alemania, cultura laboral alemana, empleo Alemania, mercado laboral Alemania, integración profesional Alemania"
        canonical="/guia-claves-alemania"
        schema={schema}
      />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/perfil?tab=recursos')}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1A2B4A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              Volver a recursos
            </button>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-xs font-semibold">
                🇩🇪 Alemania
              </span>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A2B4A] text-white text-sm font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i>
                Imprimir / Guardar PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 print:py-4 print:px-8">
        <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-10 mb-10 print:rounded-none print:mb-6">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 text-4xl">
              🇩🇪
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-amber-400/20 text-amber-300 border border-amber-400/30 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Guía cultural
                </span>
                <span className="text-white/50 text-xs">6 secciones · Lectura: ~12 min</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
                Claves culturales para trabajar en Alemania
              </h1>
              <p className="text-white/80 text-base leading-relaxed max-w-2xl">
                Aspectos esenciales del entorno laboral alemán: puntualidad, jerarquía, comunicación directa y expectativas profesionales. Todo lo que necesitas saber para integrarte con éxito en el mercado laboral alemán.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-10">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-list-check-2 text-[#1A2B4A]"></i>
            Contenido de la guía
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => {
              const c = colorMap[section.color];
              return (
                <a
                  key={section.id}
                  href={`#seccion-${section.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className={`w-7 h-7 ${c.bg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                    {section.id}
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-[#1A2B4A] font-medium transition-colors">
                    {section.title}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="space-y-8">
          {sections.map((section) => {
            const c = colorMap[section.color];
            return (
              <div
                key={section.id}
                id={`seccion-${section.id}`}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid"
              >
                <div className={`${c.light} ${c.border} border-b px-6 py-5 flex items-center gap-4`}>
                  <div className={`w-11 h-11 ${c.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${section.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>Sección {section.id}</span>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">{section.title}</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className={`w-1 rounded-full ${c.bg} flex-shrink-0 mt-1`} style={{ minHeight: '100%' }}></div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1.5">{item.subtitle}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-[#1A2B4A] rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-white text-xl"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Antes de llegar</span>
              <h2 className="text-lg font-bold text-gray-900">Checklist esencial</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                <i className="ri-check-double-line text-emerald-600 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-gray-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-xl p-8 text-center print:hidden">
          <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">¿Tienes dudas sobre el mercado alemán?</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
            Nuestro equipo tiene experiencia directa en el mercado laboral alemán y puede orientarte de forma personalizada.
          </p>
          <button
            onClick={() => navigate('/#contacto')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1A2B4A] text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Contactar con el equipo
          </button>
        </div>

        <div className="mt-8 text-center text-xs text-gray-400 pb-8 print:mt-4">
          <p>Guía elaborada por el equipo de BN Agenzia · Uso exclusivo para candidatos registrados</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { font-size: 11pt; }
        }
      `}</style>
    </div>
  );
}
